import supabase from '../supabaseClient';
import { clearCommunityContext } from '../utils/communityAccessCache';
import { clearAcademyContext } from '../utils/academyAccessCache';

/**
 * Super-admin "demo" sessions: lets a super admin open the community or
 * academy portal for any org without being added as manager/coach.
 * The client-side flag only picks the org; route guards re-verify super
 * admin status and RLS is what actually grants access.
 */

const STORAGE_KEY = 'statboxAdminDemo';
const MAX_AGE_MS = 12 * 60 * 60 * 1000;

const storage = () => (typeof window !== 'undefined' ? window.localStorage : null);

supabase.auth.onAuthStateChange((event) => {
  if (event === 'SIGNED_OUT') storage()?.removeItem(STORAGE_KEY);
});

export function readAdminDemo() {
  const store = storage();
  if (!store) return null;
  try {
    const parsed = JSON.parse(store.getItem(STORAGE_KEY) || 'null');
    if (!parsed?.orgId || !parsed?.kind || !parsed?.adminUserId) return null;
    if (!parsed.startedAt || Date.now() - parsed.startedAt > MAX_AGE_MS) {
      store.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    store.removeItem(STORAGE_KEY);
    return null;
  }
}

/** Active demo for `kind` ('community' | 'academy') belonging to `userId`, or null. */
export function getAdminDemo(kind, userId) {
  const demo = readAdminDemo();
  if (!demo || demo.kind !== kind) return null;
  if (userId && demo.adminUserId !== userId) return null;
  return demo;
}

/** `profileOrgId` unless the signed-in admin is demoing an org of this kind. */
export function resolveDemoOrgId(kind, userId, profileOrgId) {
  return getAdminDemo(kind, userId)?.orgId || profileOrgId || null;
}

export function startAdminDemo({ kind, orgId, orgName, adminUserId }) {
  const store = storage();
  if (!store) return;
  clearCommunityContext();
  clearAcademyContext();
  store.setItem(STORAGE_KEY, JSON.stringify({
    kind,
    orgId,
    orgName: orgName || '',
    adminUserId,
    startedAt: Date.now(),
  }));
}

/** Starts a demo for the signed-in admin and opens the portal in a new tab. */
export async function openAdminDemo({ kind, orgId, orgName }) {
  const path = kind === 'academy' ? '/academy/dashboard' : '/community/dashboard';
  // Open during the tap. Mobile browsers block window.open after the auth await.
  const popup = window.open('about:blank', '_blank');
  const { data } = await supabase.auth.getUser();
  const adminUserId = data?.user?.id;
  if (!adminUserId || !orgId) {
    if (popup && !popup.closed) popup.close();
    return;
  }
  startAdminDemo({ kind, orgId, orgName, adminUserId });
  if (popup && !popup.closed) {
    popup.location.replace(path);
    try { popup.opener = null; } catch { /* tab already left this page */ }
    return;
  }
  window.location.assign(path);
}

export function endAdminDemo() {
  const store = storage();
  if (!store) return;
  store.removeItem(STORAGE_KEY);
  clearCommunityContext();
  clearAcademyContext();
}
